import { useEffect, useMemo, useRef, useState } from "react";
import {
	CategoryScale,
	Chart as ChartJS,
	Filler,
	LinearScale,
	LineElement,
	PointElement,
	Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { cn } from "../../utilities/helpers";
import { useCurrency } from "../dropdown";
import {
	buildPortfolioTrend,
	formatCompactCurrency,
	formatCurrency,
	formatSignedCurrency,
	getInvestmentYearLabel,
	getInvestmentYearStart,
	useAssetPalette,
	useDailyQuery,
	useInvestmentsQuery,
} from "./shared";

ChartJS.register(CategoryScale, Filler, LinearScale, LineElement, PointElement, Tooltip);
ChartJS.defaults.font.family = '"Avenir Next", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
ChartJS.defaults.font.size = 12;

const RANGES = [
	{ key: "1M", label: "1M", title: "Last month", months: 1 },
	{ key: "3M", label: "3M", title: "Last 3 months", months: 3 },
	{ key: "6M", label: "6M", title: "Last 6 months", months: 6 },
	{ key: "FY", label: "FY", title: "This investment year (since 12 December)" },
	{ key: "1Y", label: "1Y", title: "Last 12 months", months: 12 },
	{ key: "ALL", label: "All", title: "Since the first trade" },
];

const VIEWS = [
	{ key: "portfolio", label: "Portfolio" },
	{ key: "assets", label: "By asset" },
];

const VALUE_COLOR = "#2a78d6";
const INVESTED_COLOR = "#898781";
const OTHER_COLOR = "#a3a29c";
const GRID_COLOR = "#ebeae5";
const AXIS_COLOR = "#d4d3cc";
const TICK_COLOR = "#6b6a65";
const STORAGE_KEY = "portfolioChart";

const readPrefs = () => {
	try {
		return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
	} catch {
		return {};
	}
};

const writePrefs = (prefs) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
	} catch {
		/* storage unavailable */
	}
};

const withAlpha = (hex, alpha) =>
	`${hex}${Math.round(alpha * 255)
		.toString(16)
		.padStart(2, "0")}`;

const formatDateKey = (dateKey, options) =>
	new Date(`${dateKey}T00:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", ...options });

const getRangeStartKey = (rangeKey, lastKey) => {
	if (rangeKey === "ALL" || !lastKey) return null;
	const end = new Date(`${lastKey}T00:00:00Z`);
	if (rangeKey === "FY") {
		return getInvestmentYearStart(getInvestmentYearLabel(end)).toISOString().slice(0, 10);
	}
	const range = RANGES.find((item) => item.key === rangeKey);
	end.setUTCMonth(end.getUTCMonth() - (range?.months || 0));
	return end.toISOString().slice(0, 10);
};

const formatUnits = (units) =>
	Number(units || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });

// Beyond the eight categorical slots, assets share one neutral "Other" line
// instead of reusing a hue. A single leftover asset keeps its own name.
const groupSeries = (series, paletteFor) => {
	const named = [];
	const others = [];
	series.forEach((assetSeries) => {
		const palette = paletteFor(assetSeries.asset);
		if (palette.isOther) others.push(assetSeries);
		else named.push({ ...assetSeries, key: assetSeries.asset, color: palette.line });
	});

	if (others.length === 1) {
		named.push({ ...others[0], key: others[0].asset, color: OTHER_COLOR });
	} else if (others.length > 1) {
		const length = others[0].valuesByDate.length;
		const valuesByDate = Array.from({ length }, (_, index) => {
			const owned = others.filter((assetSeries) => assetSeries.valuesByDate[index] !== null);
			return owned.length
				? owned.reduce((sum, assetSeries) => sum + assetSeries.valuesByDate[index], 0)
				: null;
		});
		named.push({
			key: "__other",
			asset: "__other",
			label: `Other (${others.length})`,
			color: OTHER_COLOR,
			valuesByDate,
			unitsByDate: null,
			pricesByDate: null,
			currentValue: valuesByDate[length - 1] || 0,
			trades: others
				.flatMap((assetSeries) =>
					assetSeries.trades.map((trade) => ({ ...trade, label: assetSeries.label }))
				)
				.map((trade) => ({ ...trade, value: valuesByDate[trade.index] || 0 }))
				.sort((left, right) => left.index - right.index),
		});
	}

	return named.sort((left, right) => right.currentValue - left.currentValue);
};

const crosshairPlugin = {
	id: "crosshair",
	afterDatasetsDraw(chart) {
		const active = chart.tooltip?.getActiveElements?.() || [];
		if (!active.length) return;
		const { ctx, chartArea } = chart;
		const x = active[0].element.x;
		ctx.save();
		ctx.beginPath();
		ctx.moveTo(x, chartArea.top);
		ctx.lineTo(x, chartArea.bottom);
		ctx.lineWidth = 1;
		ctx.strokeStyle = "#b8b7b0";
		ctx.stroke();
		ctx.restore();
	},
};

const el = (tag, className, text) => {
	const node = document.createElement(tag);
	if (className) node.className = className;
	if (text !== undefined) node.textContent = text;
	return node;
};

// Tooltip rows are built with textContent only: labels come from API data.
const renderTooltip = (container, { title, rows, notes }) => {
	container.replaceChildren();
	container.append(el("div", "mb-2 text-xs font-medium text-slate-500", title));
	rows.forEach((row) => {
		const line = el("div", "flex items-center gap-2 py-0.5");
		const key = el("span", "h-0.5 w-3 shrink-0 rounded-full");
		key.style.backgroundColor = row.color || "transparent";
		line.append(key);
		line.append(el("span", cn("font-semibold tabular-nums", row.tone || "text-slate-900"), row.value));
		line.append(el("span", "text-slate-500", row.label));
		container.append(line);
		(row.notes || []).forEach((note) => {
			container.append(el("div", "pl-5 text-xs text-slate-500", note));
		});
	});
	if (notes?.length) {
		const block = el("div", "mt-2 border-t border-slate-100 pt-2");
		notes.forEach((note) => block.append(el("div", "text-xs text-slate-600", note)));
		container.append(block);
	}
};

const tradeNote = (trade, label, currency) =>
	`${trade.type === "SELL" ? "▽ Sold" : "▲ Bought"} ${label ? `${label} · ` : ""}${formatUnits(
		trade.units
	)} units · ${formatCurrency(trade.amount, currency)}`;

const TradeGlyph = ({ type }) => (
	<svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true">
		{type === "BUY" ? (
			<path d="M6 1.5 11 10.5H1Z" fill="#52514e" />
		) : (
			<path d="M6 10.5 1 1.5h10Z" fill="#fff" stroke="#52514e" strokeWidth="1.5" />
		)}
	</svg>
);

const SegmentedControl = ({ label, options, value, onChange }) => (
	<div role="group" aria-label={label} className="inline-flex rounded-md bg-slate-100 p-0.5">
		{options.map((option) => (
			<button
				key={option.key}
				type="button"
				title={option.title}
				aria-pressed={value === option.key}
				onClick={() => onChange(option.key)}
				className={cn(
					"rounded px-2.5 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
					value === option.key
						? "bg-white text-slate-900 shadow-sm"
						: "text-slate-500 hover:text-slate-900"
				)}
			>
				{option.label}
			</button>
		))}
	</div>
);

const RangeStat = ({ label, value, tone }) => (
	<div>
		<dt className="text-xs text-slate-500">{label}</dt>
		<dd className={cn("text-sm font-semibold tabular-nums text-slate-900", tone)}>{value}</dd>
	</div>
);

const signTone = (value) =>
	value > 0 ? "text-emerald-700" : value < 0 ? "text-rose-700" : undefined;

const PortfolioChart = () => {
	const { currency } = useCurrency();
	const paletteFor = useAssetPalette();
	const { data: investments, isLoading: loadingInvestments } = useInvestmentsQuery();
	const { data: daily, isLoading: loadingDaily } = useDailyQuery();

	const [view, setView] = useState(() => readPrefs().view || "portfolio");
	const [range, setRange] = useState(() => readPrefs().range || "1Y");
	const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
	const [hoveredKey, setHoveredKey] = useState(null);
	const tooltipRef = useRef(null);

	useEffect(() => writePrefs({ view, range }), [view, range]);

	const trend = useMemo(
		() => buildPortfolioTrend({ investments, daily }),
		[investments, daily]
	);

	const assetSeries = useMemo(
		() => groupSeries(trend.series, paletteFor),
		[trend.series, paletteFor]
	);

	const { timeline, totalValuesByDate, investedByDate } = trend;
	const lastIndex = timeline.length - 1;
	const startKey = getRangeStartKey(range, timeline[lastIndex]);
	const startIndex = startKey ? Math.max(0, timeline.findIndex((key) => key >= startKey)) : 0;
	const labels = timeline.slice(startIndex);
	const spanDays =
		labels.length > 1
			? (new Date(labels[labels.length - 1]) - new Date(labels[0])) / 86400000
			: 0;

	const rangeStats = useMemo(() => {
		if (lastIndex < 0) return null;
		const valueChange = totalValuesByDate[lastIndex] - totalValuesByDate[startIndex];
		const investedChange = investedByDate[lastIndex] - investedByDate[startIndex];
		return { valueChange, investedChange, marketGain: valueChange - investedChange };
	}, [investedByDate, lastIndex, startIndex, totalValuesByDate]);

	const visibleSeries = assetSeries.filter((item) => !hiddenKeys.has(item.key));
	// Hiding the asset under the pointer must not leave every other line faded.
	const activeHover = hoveredKey && !hiddenKeys.has(hoveredKey) ? hoveredKey : null;

	// The portfolio view shades the gap between value and invested, so the axis
	// can zoom to the data without a fill that overstates the numbers.
	const portfolioBounds = useMemo(() => {
		if (view !== "portfolio" || lastIndex < 0) return null;
		let min = Infinity;
		let max = -Infinity;
		for (let index = startIndex; index <= lastIndex; index += 1) {
			min = Math.min(min, totalValuesByDate[index], investedByDate[index]);
			max = Math.max(max, totalValuesByDate[index], investedByDate[index]);
		}
		if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
		const pad = Math.max((max - min) * 0.12, Math.abs(max) * 0.02, 1);
		// Snap to a round step so ticks read $120K, not $111.6K.
		const step = 10 ** Math.floor(Math.log10(Math.max(max - min + 2 * pad, 1))) / 2;
		return {
			min: Math.max(0, Math.floor((min - pad) / step) * step),
			max: Math.ceil((max + pad) / step) * step,
		};
	}, [investedByDate, lastIndex, startIndex, totalValuesByDate, view]);

	const datasets = useMemo(() => {
		const slice = (values) => values.slice(startIndex);

		if (view === "portfolio") {
			return [
				{
					label: "Portfolio value",
					data: slice(totalValuesByDate),
					borderColor: VALUE_COLOR,
					backgroundColor: withAlpha(VALUE_COLOR, 0.12),
					fill: {
						target: 1,
						above: withAlpha(VALUE_COLOR, 0.12),
						below: withAlpha("#e34948", 0.12),
					},
					borderWidth: 2,
					pointRadius: 0,
					pointHoverRadius: 4,
					pointHoverBackgroundColor: VALUE_COLOR,
					pointHoverBorderColor: "#fff",
					pointHoverBorderWidth: 2,
					tension: 0,
					order: 1,
				},
				{
					label: "Net invested",
					data: slice(investedByDate),
					borderColor: INVESTED_COLOR,
					borderWidth: 2,
					stepped: true,
					fill: false,
					pointRadius: 0,
					pointHoverRadius: 0,
					order: 2,
				},
			];
		}

		const lines = visibleSeries.map((item) => {
			const faded = activeHover && activeHover !== item.key;
			return {
				label: item.label,
				data: slice(item.valuesByDate),
				borderColor: faded ? withAlpha(item.color, 0.2) : item.color,
				borderWidth: 2,
				borderJoinStyle: "round",
				borderCapStyle: "round",
				pointRadius: 0,
				pointHoverRadius: faded ? 0 : 3,
				pointHoverBackgroundColor: item.color,
				pointHoverBorderColor: "#fff",
				tension: 0,
				fill: false,
				order: 2,
			};
		});

		// Trade markers ride their own asset's line: ▲ filled for buys, ▽ hollow
		// for sells, so buy vs sell reads by shape as well as fill.
		const markers = visibleSeries.flatMap((item) => {
			const faded = activeHover && activeHover !== item.key;
			const color = faded ? withAlpha(item.color, 0.2) : item.color;
			return ["BUY", "SELL"].map((type) => {
				const data = new Array(labels.length).fill(null);
				item.trades.forEach((trade) => {
					if (trade.type === type && trade.index >= startIndex) {
						data[trade.index - startIndex] = trade.value;
					}
				});
				return {
					label: `${item.label} ${type === "BUY" ? "buys" : "sells"}`,
					data,
					showLine: false,
					pointStyle: "triangle",
					pointRotation: type === "SELL" ? 180 : 0,
					pointRadius: 6,
					pointHoverRadius: 7,
					pointBackgroundColor: type === "BUY" ? color : "#fff",
					pointBorderColor: type === "BUY" ? "#fff" : color,
					pointBorderWidth: 2,
					order: 0,
				};
			});
		});

		return [...lines, ...markers];
	}, [
		activeHover,
		investedByDate,
		labels.length,
		startIndex,
		totalValuesByDate,
		view,
		visibleSeries,
	]);

	const external = ({ chart, tooltip }) => {
		const container = tooltipRef.current;
		if (!container) return;
		if (!tooltip || tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
			container.style.opacity = "0";
			return;
		}

		const index = tooltip.dataPoints[0].dataIndex + startIndex;
		const title = formatDateKey(timeline[index], {
			weekday: "short",
			month: "short",
			day: "numeric",
			year: "numeric",
		});

		if (view === "portfolio") {
			const value = totalValuesByDate[index];
			const invested = investedByDate[index];
			const notes = trend.series.flatMap((item) =>
				item.trades
					.filter((trade) => trade.index === index)
					.map((trade) => tradeNote(trade, item.label, currency))
			);
			renderTooltip(container, {
				title,
				rows: [
					{ color: VALUE_COLOR, value: formatCurrency(value, currency), label: "Value" },
					{ color: INVESTED_COLOR, value: formatCurrency(invested, currency), label: "Net invested" },
					{
						value: formatSignedCurrency(value - invested, currency),
						label: "Gain",
						tone: signTone(value - invested),
					},
				],
				notes,
			});
		} else {
			const rows = visibleSeries
				.map((item) => {
					const trades = item.trades.filter((trade) => trade.index === index);
					const value = item.valuesByDate[index];
					if (!value && !trades.length) return null;
					const units = item.unitsByDate?.[index];
					const price = item.pricesByDate?.[index];
					return {
						sortValue: value,
						color: item.color,
						value: formatCurrency(value, currency),
						label:
							units !== undefined && units !== null
								? `${item.label} · ${formatUnits(units)} @ ${formatCurrency(price, currency)}`
								: item.label,
						notes: trades.map((trade) => tradeNote(trade, trade.label, currency)),
					};
				})
				.filter(Boolean)
				.sort((left, right) => right.sortValue - left.sortValue);
			renderTooltip(container, { title, rows });
		}

		const { chartArea } = chart;
		const width = container.offsetWidth;
		const height = container.offsetHeight;
		const gap = 16;
		let left = tooltip.caretX + gap;
		if (left + width > chart.width) left = tooltip.caretX - gap - width;
		const top = Math.min(
			Math.max(chartArea.top, tooltip.caretY - height / 2),
			chart.height - height
		);
		container.style.opacity = "1";
		container.style.transform = `translate(${Math.max(0, left)}px, ${Math.max(0, top)}px)`;
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		animation: { duration: 250 },
		interaction: { mode: "index", intersect: false },
		layout: { padding: { top: 8, right: 8 } },
		plugins: {
			legend: { display: false },
			tooltip: { enabled: false, external },
		},
		scales: {
			x: {
				grid: { display: false },
				border: { color: AXIS_COLOR },
				ticks: {
					color: TICK_COLOR,
					maxRotation: 0,
					autoSkip: true,
					autoSkipPadding: 24,
					maxTicksLimit: 8,
					callback(value) {
						const key = this.getLabelForValue(value);
						return formatDateKey(
							key,
							spanDays > 200 ? { month: "short", year: "2-digit" } : { month: "short", day: "numeric" }
						);
					},
				},
			},
			y: {
				...(portfolioBounds
					? { min: portfolioBounds.min, max: portfolioBounds.max }
					: { beginAtZero: true }),
				grid: { color: GRID_COLOR },
				border: { display: false },
				ticks: {
					color: TICK_COLOR,
					maxTicksLimit: 6,
					callback: (value) => formatCompactCurrency(Number(value), currency),
				},
			},
		},
	};

	const toggleKey = (key) => {
		setHiddenKeys((current) => {
			const next = new Set(current);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
		setHoveredKey(null);
	};

	const soloKey = (key) => {
		setHiddenKeys(new Set(assetSeries.filter((item) => item.key !== key).map((item) => item.key)));
	};

	const isLoading = loadingInvestments || loadingDaily;
	const rangeLabel = RANGES.find((item) => item.key === range)?.title.toLowerCase();

	return (
		<section
			aria-labelledby="portfolio-chart-heading"
			className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:p-6"
		>
			<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<h2 id="portfolio-chart-heading" className="text-lg font-semibold text-slate-900">
						{view === "portfolio" ? "Portfolio value" : "Value by asset"}
					</h2>
					<p className="mt-0.5 text-sm text-slate-500">
						{view === "portfolio"
							? "Market value against the money you put in. The gap is your gain."
							: "Each holding's market value, with its buys and sells marked on its own line."}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<SegmentedControl label="Chart view" options={VIEWS} value={view} onChange={setView} />
					<SegmentedControl label="Time range" options={RANGES} value={range} onChange={setRange} />
				</div>
			</div>

			{rangeStats && (
				<dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2" aria-label={`Change over ${rangeLabel}`}>
					<RangeStat
						label="Value change"
						value={formatSignedCurrency(rangeStats.valueChange, currency)}
						tone={signTone(rangeStats.valueChange)}
					/>
					<RangeStat
						label="Added capital"
						value={formatSignedCurrency(rangeStats.investedChange, currency)}
					/>
					<RangeStat
						label="Market gain"
						value={formatSignedCurrency(rangeStats.marketGain, currency)}
						tone={signTone(rangeStats.marketGain)}
					/>
				</dl>
			)}

			<div className="relative mt-4 h-72 md:h-96" onMouseLeave={() => setHoveredKey(null)}>
				{isLoading ? (
					<div className="h-full w-full animate-pulse rounded-lg bg-slate-100" />
				) : labels.length === 0 ? (
					<div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-500">
						Add your first transaction to see the portfolio over time.
					</div>
				) : (
					<Line
						data={{ labels, datasets }}
						options={options}
						plugins={[crosshairPlugin]}
						aria-label={
							view === "portfolio"
								? "Line chart of portfolio value and net invested capital over time"
								: "Line chart of each asset's value over time with buy and sell markers"
						}
						role="img"
					/>
				)}
				<div
					ref={tooltipRef}
					role="presentation"
					className="pointer-events-none absolute left-0 top-0 z-10 min-w-[12rem] max-w-xs rounded-lg border border-slate-200 bg-white/95 px-3 py-2.5 text-sm opacity-0 shadow-lg backdrop-blur transition-opacity duration-100"
				/>
			</div>

			{view === "portfolio" ? (
				<ul className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
					<li className="flex items-center gap-2">
						<span className="h-0.5 w-4 rounded-full" style={{ backgroundColor: VALUE_COLOR }} />
						Portfolio value
					</li>
					<li className="flex items-center gap-2">
						<span className="h-0.5 w-4 rounded-full" style={{ backgroundColor: INVESTED_COLOR }} />
						Net invested
					</li>
				</ul>
			) : (
				<div className="mt-4 space-y-3">
					<div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
						<span className="flex items-center gap-3">
							<span className="flex items-center gap-1">
								<TradeGlyph type="BUY" /> Buy
							</span>
							<span className="flex items-center gap-1">
								<TradeGlyph type="SELL" /> Sell
							</span>
							<span className="hidden sm:inline">
								Click an asset to show or hide it, double-click to show only that asset.
							</span>
						</span>
						{hiddenKeys.size > 0 && (
							<button
								type="button"
								onClick={() => setHiddenKeys(new Set())}
								className="rounded font-medium text-slate-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
							>
								Show all
							</button>
						)}
					</div>
					<ul className="flex flex-wrap gap-2" aria-label="Assets shown in chart">
						{assetSeries.map((item) => {
							const hidden = hiddenKeys.has(item.key);
							return (
								<li key={item.key}>
									<button
										type="button"
										aria-pressed={!hidden}
										onClick={() => toggleKey(item.key)}
										onDoubleClick={() => soloKey(item.key)}
										onMouseEnter={() => !hidden && setHoveredKey(item.key)}
										onMouseLeave={() => setHoveredKey(null)}
										onFocus={() => !hidden && setHoveredKey(item.key)}
										onBlur={() => setHoveredKey(null)}
										className={cn(
											"inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
											hidden
												? "border-slate-200 bg-slate-50 text-slate-400"
												: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
										)}
									>
										<span
											className="h-0.5 w-3 rounded-full"
											style={{ backgroundColor: hidden ? "#cbd5e1" : item.color }}
										/>
										<span className={cn("font-medium", hidden && "line-through")}>{item.label}</span>
										<span className="tabular-nums text-slate-500">
											{formatCompactCurrency(item.currentValue, currency)}
										</span>
									</button>
								</li>
							);
						})}
					</ul>
				</div>
			)}
		</section>
	);
};

export default PortfolioChart;
